import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { uploadCopilotFile } from 'shared/api/copilot';
import { canonicalImageMimeType, computeFileContentHash, IMAGE_VALIDATION_REASON, MAX_IMAGES_PER_MESSAGE, MAX_INLINE_IMAGE_BYTES_PER_MESSAGE, validateImageFile, type ImageValidationReason } from 'shared/copilot/utils/imageAttachment';
import logger from 'shared/logger';
const log = logger().child({
  namespace: 'copilot/imageAttachments'
});
export type ImageAttachmentStatus = 'uploading' | 'success' | 'error';
export interface CopilotImageAttachment {
  id: string;
  filename: string;
  mimeType: string;
  /** Object URL for the composer thumbnail; revoked when the tile goes away. */
  previewUrl: string | null;
  contentHash: string | null;
  status: ImageAttachmentStatus;
  /** Set once the upload succeeds — this is what the message carries. */
  gcsUri: string | null;
}

/** Internal tile state: the `File` is held until the upload succeeds, so a
 * failed upload can be retried without asking the user to pick the file again. */
interface AttachmentEntry extends CopilotImageAttachment {
  file: File | null;
  sizeBytes: number;
}
export type ImageAttachmentError = ImageValidationReason | 'uploadFailed';
export interface UseCopilotImageAttachmentsArgs {
  clientId: number;
}
export interface UseCopilotImageAttachmentsResult {
  attachments: CopilotImageAttachment[];
  /** Successfully uploaded attachments, in the order the user added them. */
  uploadedAttachments: CopilotImageAttachment[];
  isUploading: boolean;
  /** Reason the last batch was (partly) refused, for the message under the composer. */
  errorReason: ImageAttachmentError | null;
  attachFiles: (files: readonly File[]) => Promise<void>;
  removeAttachment: (id: string) => void;
  retryAttachment: (id: string) => Promise<void>;
  clearAttachments: () => void;
  /**
   * Empties the composer like `clearAttachments`, but revokes nothing —
   * ownership of every tile's preview URL passes to the caller. Used only
   * when sending: an image that joins the message hands its blob to the
   * thread as an instant preview until the real signed URL is ready, so the
   * composer must not revoke it out from under that hand-off. The caller is
   * responsible for revoking (via {@link revokePreviewUrl}) whichever
   * returned entries it does not keep.
   */
  takeAttachments: () => CopilotImageAttachment[];
  dismissError: () => void;
}
const createPreviewUrl = (file: File): string | null => {
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
};

/** Revokes a composer preview URL. Safe to call on one already revoked, or `null`. */
export const revokePreviewUrl = (previewUrl: string | null): void => {
  if (previewUrl == null) return;
  try {
    URL.revokeObjectURL(previewUrl);
  } catch {
    // A preview URL that was never created, or already revoked, is not a problem.
  }
};

/**
 * Owns the composer's image attachments: validation, upload, retry, and the
 * blob previews the tiles render.
 *
 * Deliberately separate from `useCopilotCsvAttachment`. The two look alike in
 * the composer but travel to the agent by completely different routes — CSV
 * through `agent.state` with four routing modes and session-level stickiness,
 * images as content parts on the one message they were sent with — and folding
 * them together would put all of that CSV routing logic in the path of a
 * feature that needs none of it.
 */
export const useCopilotImageAttachments = ({
  clientId
}: UseCopilotImageAttachmentsArgs): UseCopilotImageAttachmentsResult => {
  const [attachments, setAttachments] = useState<AttachmentEntry[]>([]);
  const [errorReason, setErrorReason] = useState<ImageAttachmentError | null>(null);
  // Mirrors `attachments` so callbacks can read the current list without
  // re-creating themselves on every upload state change.
  const attachmentsRef = useRef<AttachmentEntry[]>([]);
  // Serializes the admission phase of overlapping `attachFiles` calls. See
  // `admitFiles` for why hashing makes that necessary.
  const admissionRef = useRef<Promise<void>>(Promise.resolve());
  // Bumped by `clearAttachments`/`takeAttachments` — both discard whatever is
  // currently attached. `admitFiles` captures this before its async hashing
  // step and checks it again after: a bump in between means the composer that
  // asked for these files is gone, so its result must be discarded rather than
  // committed onto (or sent with) whatever the composer holds now.
  const generationRef = useRef(0);
  // Lets `admitFiles` tell a real unmount apart from a mere reset: only an
  // unmount is not covered by the existing cleanup effect below, since that
  // effect's own cleanup already ran by the time a still-in-flight `admitFiles`
  // resolves and would create new preview blobs nothing will ever revoke.
  const mountedRef = useRef(true);
  // Files picked but not yet admitted (validated, hashed, committed as
  // tiles) are invisible to `attachments` — a count, not a boolean, because
  // two picks can overlap and the first to finish must not clear the flag
  // while the second is still hashing. `isUploading` folds this in so the
  // composer keeps send disabled through the hashing gap too, not just once
  // a tile exists to show `status: 'uploading'`.
  const [pendingAdmissionCount, setPendingAdmissionCount] = useState(0);
  const commit = useCallback((next: AttachmentEntry[]) => {
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);
  const updateEntry = useCallback((id: string, patch: Partial<AttachmentEntry>) => {
    const next_0 = attachmentsRef.current.map(entry => entry.id === id ? {
      ...entry,
      ...patch
    } : entry);
    attachmentsRef.current = next_0;
    setAttachments(next_0);
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      attachmentsRef.current.forEach(entry_0 => {
        revokePreviewUrl(entry_0.previewUrl);
      });
    };
  }, []);
  const uploadEntry = useCallback(async (id_0: string, file: File): Promise<void> => {
    try {
      const uploaded = await uploadCopilotFile(clientId, file);
      if (!attachmentsRef.current.some(entry_2 => entry_2.id === id_0)) {
        // Removed while the upload was in flight — drop the result.
        return;
      }
      updateEntry(id_0, {
        status: 'success',
        gcsUri: uploaded.gcsUri,
        filename: uploaded.filename,
        // Flow sniffs the stored bytes, so its `contentType` is the only
        // authoritative one. `file.type` is a browser guess that is empty
        // when it cannot infer a type and the non-standard `image/jpg` for
        // some JPEGs (iOS camera roll among them) — and the composer
        // deliberately admits those on extension alone. Sending either
        // verbatim gives the agent an image part it cannot inline, on a
        // tile that already reported success. A missing value is not `''`:
        // `undefined !== ''` is true, and spreading it would replace the
        // canonical MIME with `undefined` on a tile that already succeeded.
        ...(typeof uploaded.contentType === 'string' && uploaded.contentType !== '' ? {
          mimeType: uploaded.contentType
        } : {}),
        file: null
      });
    } catch (error) {
      log.warn({
        error: error instanceof Error ? error.message : String(error),
        clientId
      }, 'failed to upload copilot image attachment');
      if (!attachmentsRef.current.some(entry_1 => entry_1.id === id_0)) return;
      updateEntry(id_0, {
        status: 'error'
      });
      setErrorReason('uploadFailed');
    }
  }, [clientId, updateEntry]);

  /**
   * Validates, hashes and commits one batch, returning the tiles it admitted.
   *
   * Runs under `admissionRef` because `computeFileContentHash` is awaited
   * per file: two picks in quick succession would otherwise both measure
   * duplicates and remaining capacity against the list as it was before
   * either batch landed, letting the same file through twice or pushing the
   * composer past `MAX_IMAGES_PER_MESSAGE` or `MAX_INLINE_IMAGE_BYTES_PER_MESSAGE`.
   */
  const admitFiles = useCallback(async (files: readonly File[]): Promise<AttachmentEntry[]> => {
    const generation = generationRef.current;
    let rejection: ImageAttachmentError | null = null;
    const accepted: AttachmentEntry[] = [];
    // Read after every await, not once up front: a tile removed while a file
    // was hashing must free its slot, its bytes and its hash for the rest of the batch.
    const occupied = (): AttachmentEntry[] => [...attachmentsRef.current, ...accepted];
    for (const file_0 of files) {
      const validation = validateImageFile(file_0);
      if (!validation.ok) {
        rejection = validation.reason;
        continue;
      }
      if (occupied().length >= MAX_IMAGES_PER_MESSAGE) {
        rejection = IMAGE_VALIDATION_REASON.tooMany;
        continue;
      }

      // Hashing before upload is what makes the duplicate check possible:
      // Flow deduplicates by content and would return the existing URI, so
      // without this the second tile would have to vanish after the fact.
      const contentHash = await computeFileContentHash(file_0);
      const current = occupied();
      if (contentHash != null && current.some(entry_3 => entry_3.contentHash === contentHash)) {
        rejection = IMAGE_VALIDATION_REASON.duplicate;
        continue;
      }
      const bytesUsed = current.reduce((sum, entry_4) => sum + entry_4.sizeBytes, 0);
      if (file_0.size > MAX_INLINE_IMAGE_BYTES_PER_MESSAGE - bytesUsed) {
        rejection = IMAGE_VALIDATION_REASON.inlineBudgetExceeded;
        continue;
      }
      accepted.push({
        id: uuidv4(),
        filename: file_0.name,
        mimeType: canonicalImageMimeType(file_0),
        previewUrl: createPreviewUrl(file_0),
        contentHash,
        status: 'uploading',
        gcsUri: null,
        file: file_0,
        sizeBytes: file_0.size
      });
    }

    // The composer that requested this batch was reset (new chat, thread
    // switch, or a send) or has unmounted entirely while hashing was still
    // running — committing now would attach these files to whatever the
    // composer holds next, or send them with the wrong message. Nothing
    // will ever show these tiles, so their preview blobs must be revoked
    // here rather than leaked.
    if (generation !== generationRef.current || !mountedRef.current) {
      accepted.forEach(entry_5 => {
        revokePreviewUrl(entry_5.previewUrl);
      });
      return [];
    }
    setErrorReason(rejection);
    if (accepted.length === 0) return [];
    commit([...attachmentsRef.current, ...accepted]);
    return accepted;
  }, [commit]);
  const attachFiles = useCallback(async (files_0: readonly File[]): Promise<void> => {
    if (files_0.length === 0) return;

    // Counted from before the batch even joins the admission queue: a
    // batch waiting behind an earlier one is just as invisible to
    // `attachments` as one actively hashing, and must keep send disabled
    // too.
    setPendingAdmissionCount(count => count + 1);
    let accepted_0: AttachmentEntry[];
    try {
      const admission = admissionRef.current.then(async () => await admitFiles(files_0));
      // The lock must never carry a rejection, or one failed batch would break
      // admission for every later pick.
      admissionRef.current = admission.then(() => undefined, () => undefined);
      accepted_0 = await admission;
    } finally {
      setPendingAdmissionCount(count => count - 1);
    }

    // Uploads run outside the lock: a batch waits only for the tiles ahead
    // of it to be admitted, not for their bytes to finish uploading. Once
    // admitted, an accepted tile's own `status: 'uploading'` already keeps
    // `isUploading` true for this part, independent of the count above.
    await Promise.all(accepted_0.map(async entry_6 => {
      await uploadEntry(entry_6.id, entry_6.file as File);
    }));
  }, [admitFiles, uploadEntry]);
  const removeAttachment = useCallback((id_1: string) => {
    const removed = attachmentsRef.current.find(entry_7 => entry_7.id === id_1);
    revokePreviewUrl(removed?.previewUrl ?? null);
    commit(attachmentsRef.current.filter(entry_8 => entry_8.id !== id_1));
  }, [commit]);
  const retryAttachment = useCallback(async (id_2: string): Promise<void> => {
    const entry_9 = attachmentsRef.current.find(candidate => candidate.id === id_2);
    if (entry_9?.file == null) return;
    setErrorReason(null);
    updateEntry(id_2, {
      status: 'uploading'
    });
    await uploadEntry(id_2, entry_9.file);
  }, [updateEntry, uploadEntry]);
  const clearAttachments = useCallback(() => {
    generationRef.current += 1;
    attachmentsRef.current.forEach(entry_10 => {
      revokePreviewUrl(entry_10.previewUrl);
    });
    commit([]);
    setErrorReason(null);
  }, [commit]);
  const takeAttachments = useCallback((): CopilotImageAttachment[] => {
    generationRef.current += 1;
    const current_0 = attachmentsRef.current;
    commit([]);
    setErrorReason(null);
    return current_0;
  }, [commit]);
  const dismissError = useCallback(() => {
    setErrorReason(null);
  }, []);
  return {
    attachments,
    uploadedAttachments: attachments.filter(entry_11 => entry_11.status === 'success'),
    isUploading: pendingAdmissionCount > 0 || attachments.some(entry_12 => entry_12.status === 'uploading'),
    errorReason,
    attachFiles,
    removeAttachment,
    retryAttachment,
    clearAttachments,
    takeAttachments,
    dismissError
  };
};